import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Search, Scissors } from "lucide-react";
import KnockoutWorkspace from "../components/KnockoutWorkspace";
import ViewToggle, { gridCols } from "../components/ViewToggle";
import useViewPreference from "../hooks/useViewPreference";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function KnockoutPage() {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);
  const [search, setSearch] = useState("");
  const { viewMode, setViewMode, imageSize, setImageSize } = useViewPreference("knockout");

  useEffect(() => {
    axios
      .get(`${API}/images`, { withCredentials: true })
      .then(({ data }) => {
        // Upscaled images first
        const sorted = [...data].sort((a, b) => (b.is_upscaled ? 1 : 0) - (a.is_upscaled ? 1 : 0));
        setImages(sorted);
      })
      .catch(() => toast.error("Failed to load images"))
      .finally(() => setLoading(false));
  }, []);

  const filtered = images.filter((img) => {
    if (!search) return true;
    return (img.title || img.prompt || "").toLowerCase().includes(search.toLowerCase());
  });

  // When an image is selected, render the workspace filling the page
  if (selectedImage) {
    return (
      <div className="-m-4 md:-m-8 flex flex-col" style={{ height: "calc(100vh - 56px)" }}>
        <KnockoutWorkspace
          image={selectedImage}
          onClose={() => setSelectedImage(null)}
          embedded
        />
      </div>
    );
  }

  return (
    <div className="pb-20 md:pb-0">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-1">
          <Scissors size={20} />
          <h1 className="font-heading text-2xl md:text-3xl font-bold tracking-tight">
            Knockout Generator
          </h1>
        </div>
        <p className="text-gray-500 text-sm">
          Select a Winner to open the knockout workspace
        </p>
      </div>

      {/* Search + View Toggle */}
      <div className="flex items-center gap-4 mb-6">
        <div className="relative max-w-sm flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={15} />
          <input
            type="text"
            placeholder="Search images…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2 border border-gray-200 text-sm focus:outline-none focus:border-black bg-white"
          />
        </div>
        <ViewToggle viewMode={viewMode} setViewMode={setViewMode} imageSize={imageSize} setImageSize={setImageSize} />
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-black border-t-transparent rounded-full animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="border border-dashed border-gray-300 p-12 text-center text-gray-400 text-sm">
          No images found
        </div>
      ) : viewMode === "list" ? (
        <div className="flex flex-col divide-y divide-gray-100 border border-gray-200">
          {filtered.map((img) => (
            <button
              key={img.id}
              onClick={() => setSelectedImage(img)}
              className="flex items-center gap-3 p-3 hover:bg-gray-50 text-left w-full"
            >
              <img
                src={img.image_data}
                alt={img.title || img.prompt}
                className="w-12 h-12 object-cover flex-shrink-0 border border-gray-200"
                loading="lazy"
              />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate text-gray-700">
                  {img.title || img.prompt || "Untitled"}
                </p>
                {img.is_upscaled && (
                  <p className="text-[10px] text-green-600 uppercase tracking-wider mt-0.5">Upscaled</p>
                )}
              </div>
            </button>
          ))}
        </div>
      ) : (
        <div className={`grid ${gridCols(imageSize)} gap-4`}>
          {filtered.map((img) => (
            <button
              key={img.id}
              onClick={() => setSelectedImage(img)}
              className="border border-gray-200 hover:border-black transition-colors text-left group"
            >
              <div className="aspect-square bg-gray-100 overflow-hidden">
                <img
                  src={img.image_data}
                  alt={img.title || img.prompt}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                  loading="lazy"
                />
              </div>
              <div className="p-2">
                <p className="text-xs truncate text-gray-700 font-medium">
                  {img.title || img.prompt || "Untitled"}
                </p>
                {img.is_upscaled && (
                  <p className="text-[10px] text-green-600 uppercase tracking-wider mt-0.5">
                    Upscaled
                  </p>
                )}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
